import { ethers } from "ethers";
import abi from "./methods/reward.json";
import {getGasFee} from "./methods/gasFee";


/**
 * Validates an email address.
 * @param value - The email address to validate.
 * @returns A boolean indicating whether the email address is valid.
 */
export const validateEmail = (value: string): boolean => {
  const emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return emailRegex.test(value);
};

/**
 * Rounds a number to a specified number of decimal places.
 * @param num - The number to round.
 * @param dec - The number of decimal places to round to.
 * @returns The rounded number.
 */
export const preciseRound = (num: number, dec: number): number => {
  const numSign = num >= 0 ? 1 : -1;
  return Number((Math.round(num * Math.pow(10, dec) + numSign * 0.0001) / Math.pow(10, dec)).toFixed(dec));
};

/**
 * Copies the specified text to the clipboard.
 * @param text - The text to be copied.
 */
export const copyToClipboard = (text: string): void => {
  const textField = document.createElement("textarea");
  textField.innerText = text;
  document.body.appendChild(textField);
  textField.select();
  document.execCommand("copy");
  textField.remove();
};

/**
 * Modifies the text by adding ellipsis (...) if its length exceeds the specified limit.
 * @param text - The original text.
 * @param noToView - The maximum number of characters to display before adding ellipsis.
 * @returns The modified text with ellipsis if necessary.
 */
export const sliceText = (text: string, noToView: number): string => {
  if (text.length > noToView) {
    let formattedText = text.slice(0, noToView) + "...";
    return formattedText;
  }
  return text;
};


/**
 * Format a number with commas as thousands separators and customizable decimal and thousand separators.
 * @param n - The number to format.
 * @param c - The number of decimal places (default: 2).
 * @param d - The decimal separator (default: ".").
 * @param t - The thousand separator (default: ",").
 * @returns The formatted number as a string.
 */

export function numberWithCommasWithoutDecimal(number: number) {
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

 export const numberWithCommas = (
  n: number,
  c: number = 2,
  d: string = ".",
  t: string = ","
): string => {
  c = isNaN(Math.abs(c)) ? 2 : Math.abs(c);
  d = d === undefined ? "." : d;
  t = t === undefined ? "," : t;
  let s = n < 0 ? "-" : "";
  let i = String(parseInt((Math.abs(Number(n)) || 0).toFixed(c), 10));
  let j = i.length > 3 ? i.length % 3 : 0;
  return (
    s +
    (j ? i.substr(0, j) + t : "") +
    i.substr(j).replace(/(\d{3})(?=\d)/g, "$1" + t) +
    (c
      ? d +
        Math.abs(n - parseFloat(i))
          .toFixed(c)
          .slice(2)
      : "")
  );
};

export const renderDays = () => {
  var arr = [];
  for (let i = 1; i < 32; i++) {
    let title = i > 1 ? " days" : " day"
    arr.push({id: i, value: i + title, enum: i});
  }
  arr.push({id: arr.length + 1, value: "Unlimited", enum: "Unlimited"});
  return arr;
};

export const renderHrs = () => {
  var arr = [];
  for (let i = 1; i < 25; i++) {
    let title = i > 1 ? " hrs" : " hr"
    arr.push({ id: i, value : i +  title, enum: i});
  }
  return arr;
};

//Get Reward from Contract
export const withdrawablereward = (holderId: string) => {
  return async () => {
    let withdrawables = await walletInfo(holderId);
    return Number(withdrawables[1]) / Math.pow(10, 18);
  };
};

/**
 * This function is used to create contracts web3 objects used for calling contract methods.
 * @param abi
 * @param contractAddress
 */


 interface CustomWindow extends Window {
  ethereum?: any;
}

 async function createContract(abi : any, contractAddress: string) {
  const provider = new ethers.BrowserProvider((window as CustomWindow).ethereum);
  const signer = await provider.getSigner();
  return new ethers.Contract(contractAddress, abi, signer);
}

//Get wallet info
export const walletInfo = async(holderId: string) => {
  try {
    const contract = await createContract(
      abi,
      process.env.REACT_APP_REWARD_CONTRACT as string
    );
    let rewards = await contract.getHolderReward(holderId);
    return rewards;
  } catch (err) {
    return [];
  }
}

//Withdraw claim
export async function withdrawClaims(address: string) {
  try {
    const contract = await createContract(
      abi,
      process.env.REACT_APP_REWARD_CONTRACT as string
    );
    let gasFee = await getGasFee();
    let claim = await contract.claimReward({ from: address, gasPrice: gasFee });

    return claim;
  } catch (err) {
    return [];
  }
}

//Open Camers
export const openCamera = () => {
  return async () => {
    let stream = await navigator.mediaDevices.getUserMedia({
      video: true,
      audio: false
    });
    let videotostream = window.document.getElementById("videocamera") as HTMLVideoElement;
    videotostream.srcObject = stream;
    return stream;
  };
};

//Take a picture 

export const takePicture = (stream: MediaStream) => {
  return (): string => {
    const canvas = document.getElementById("captureImage") as HTMLCanvasElement | null;
    const video = document.getElementById("videocamera") as HTMLVideoElement | null;

    if (!canvas || !video) {
      throw new Error("Canvas or video element not found.");
    }

    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Canvas context not available.");
    }

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    const image_data_url = canvas.toDataURL("image/jpeg");

    video.srcObject = null;

    const tracks = stream.getTracks();
    tracks.forEach((track) => track.stop());
    
    return image_data_url;
  };
};

export function calculateDateRange(range: string): [number | null, number | null] {
  const currentDate = new Date();
  const currentTimestamp = Math.floor(currentDate.getTime());

  let newStartDate: number | null = null;
  let newEndDate: number | null = null;

  switch (range) {
    case 'All':
      newStartDate = null;
      newEndDate = null;
      break;
    case 'Last Week':
      const lastWeekStart = new Date(currentDate);
      lastWeekStart.setDate(currentDate.getDate() - 7);
      newStartDate = Math.floor(lastWeekStart.getTime());
      newEndDate = currentTimestamp;
      break;
    case 'Last Month':
      const lastMonthStart = new Date(currentDate);
      lastMonthStart.setMonth(currentDate.getMonth() - 1);
      newStartDate = Math.floor(lastMonthStart.getTime());
      newEndDate = currentTimestamp;
      break;
    case 'Last 3 Months':
      const lastThreeMonthsStart = new Date(currentDate);
      lastThreeMonthsStart.setMonth(currentDate.getMonth() - 3);
      newStartDate = Math.floor(lastThreeMonthsStart.getTime());
      newEndDate = currentTimestamp;
      break;
    case 'Last Year':
      const lastYearStart = new Date(currentDate);
      lastYearStart.setFullYear(currentDate.getFullYear() - 1);
      newStartDate = Math.floor(lastYearStart.getTime());
      newEndDate = currentTimestamp;
      break;
    default:
      break;
  }

  return [newStartDate, newEndDate];
}

type MonthMap = {
  [key: number]: string;
};

export const  months: MonthMap = {
  1: "Jan",
  2: "Feb",
  3: "Mar",
  4: "Apr",
  5: "May",
  6: "Jun",
  7: "Jul",
  8: "Aug",
  9: "Sep",
  10: "Oct",
  11: "Nov",
  12: "Dec"
}